import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { ModulePdfButton } from "../components/ModulePdfButton";
import { getExerciseName } from "../lib/exerciseI18n";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, ArrowRight, Check, Star, Sparkles, Shield, ShieldCheck, BookOpen, HelpCircle, HeartPulse, Target } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import { modulesEN } from "../data/psychoeducation-en";
import { modulesES } from "../data/psychoeducation-es";
import { modulesCA } from "../data/psychoeducation-ca";
import { modulesIT } from "../data/psychoeducation-it";
import { modulesPT } from "../data/psychoeducation-pt";
import { modulesNL } from "../data/psychoeducation-nl";

const TRANSLATED_MODULES = { en: modulesEN, es: modulesES, ca: modulesCA, it: modulesIT, pt: modulesPT, nl: modulesNL };
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

// ============ MODULE CONTENT ============

import { modulesFR as MODULES } from "../data/psychoeducation-fr";

// ============ COMPONENT ============

const PsychoeducationPage = ({ user, logout }) => {
  const { exerciseType } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();

  const [currentChapter, setCurrentChapter] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResult, setShowQuizResult] = useState({});
  const [chaptersCompleted, setChaptersCompleted] = useState([]);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  // Merge translated content for schema/tcd/dissociation modules
  const translatedModules = locale === "fr" ? null : (TRANSLATED_MODULES[locale] || modulesEN);
  const getModuleData = () => {
    const base = MODULES[exerciseType];
    if (!base) return null;
    if (!translatedModules || !translatedModules[exerciseType]) return base;
    const translated = translatedModules[exerciseType];
    return {
      ...base,
      title: translated.title,
      subtitle: translated.subtitle,
      chapters: base.chapters.map((ch, i) => {
        const tCh = translated.chapters?.[i];
        if (!tCh) return ch;
        return {
          ...ch,
          title: tCh.title || ch.title,
          paragraphs: tCh.paragraphs || ch.paragraphs,
          quiz: ch.quiz ? {
            ...ch.quiz,
            question: tCh.quiz?.question || ch.quiz.question,
            options: tCh.quiz?.options || ch.quiz.options,
            explanation: tCh.quiz?.explanation || ch.quiz.explanation,
          } : ch.quiz,
        };
      }),
    };
  };
  const moduleData = getModuleData();

  useEffect(() => {
    if (!moduleData) return;
    axios.get(`${API}/exercises/responses?exercise_type=${exerciseType}`, { withCredentials: true })
      .then(res => setHistory(res.data || []))
      .catch(() => {});
  }, [exerciseType]);

  if (!moduleData) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="text-center py-20">
          <p className="text-muted-foreground">Module introuvable.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Retour</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { chapters, isChild, color, bgColor } = moduleData;
  const chapter = chapters[currentChapter];
  const progress = ((chaptersCompleted.length) / chapters.length) * 100;
  const isLastChapter = currentChapter === chapters.length - 1;
  const allCompleted = chaptersCompleted.length === chapters.length;

  const handleQuizAnswer = (chapterId, answerIdx) => {
    if (showQuizResult[chapterId]) return;
    setQuizAnswers(prev => ({ ...prev, [chapterId]: answerIdx }));
    setShowQuizResult(prev => ({ ...prev, [chapterId]: true }));
    if (!chaptersCompleted.includes(chapterId)) {
      setChaptersCompleted(prev => [...prev, chapterId]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveRes = await axios.post(`${API}/exercises/respond`, {
        exercise_type: exerciseType,
        data: {
          chapters_completed: chaptersCompleted,
          quiz_answers: quizAnswers,
          progress: Math.round(progress),
          total_chapters: chapters.length,
        },
      }, { withCredentials: true });
      toast.success(t("psychoed.completed"));
      if (user?.role === "patient") promptShare(saveRes.data, exerciseType);
      const res = await axios.get(`${API}/exercises/responses?exercise_type=${exerciseType}`, { withCredentials: true });
      setHistory(res.data || []);
    } catch (e) {
      toast.error(t("psychoed.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    if (currentChapter < chapters.length - 1) setCurrentChapter(currentChapter + 1);
  };
  const goPrev = () => {
    if (currentChapter > 0) setCurrentChapter(currentChapter - 1);
  };

  const ModuleIcon = moduleData.icon;

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6 overflow-hidden" data-testid="psychoeducation-page">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" className="shrink-0 mt-1" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-lg sm:text-2xl font-bold break-words">{moduleData.title}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground break-words">{moduleData.subtitle}</p>
          </div>
          <ModulePdfButton moduleId={exerciseType} iconOnly className="shrink-0 mt-1" />
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
            <ModuleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t("psychoed.progress")}</span>
            <span className="font-medium">{chaptersCompleted.length}/{chapters.length} {t("psychoed.chapters")}</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        {/* Chapter Navigation - Dropdown on mobile, pills on desktop */}
        {/* Mobile: dropdown selector */}
        <div className="sm:hidden">
          <select
            value={currentChapter}
            onChange={(e) => setCurrentChapter(Number(e.target.value))}
            className="w-full p-2.5 rounded-lg border bg-card text-sm font-medium appearance-none"
            style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'}}
            data-testid="chapter-select-mobile"
          >
            {chapters.map((ch, idx) => {
              const isCompleted = chaptersCompleted.includes(ch.id);
              return (
                <option key={ch.id} value={idx}>
                  {ch.icon} {idx + 1}. {ch.title} {isCompleted ? " \u2713" : ""}
                </option>
              );
            })}
          </select>
        </div>
        {/* Desktop/Tablet: scrollable pills */}
        <div className="hidden sm:flex gap-2 overflow-x-auto pb-2" style={{WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none'}}>
          {chapters.map((ch, idx) => {
            const isCompleted = chaptersCompleted.includes(ch.id);
            const isCurrent = idx === currentChapter;
            return (
              <button
                key={ch.id}
                onClick={() => setCurrentChapter(idx)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all shrink-0 ${
                  isCurrent
                    ? "bg-primary text-primary-foreground shadow-md"
                    : isCompleted
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                data-testid={`chapter-nav-${idx}`}
              >
                {isCompleted ? <Check className="w-3.5 h-3.5" /> : <span className="text-base">{ch.icon}</span>}
                {ch.title.length > 20 ? ch.title.slice(0, 20) + "..." : ch.title}
              </button>
            );
          })}
        </div>

        {/* Chapter Content */}
        <Card className={`overflow-hidden ${isChild ? bgColor : ""}`}>
          <CardContent className="p-0">
            {/* Chapter illustration */}
            {chapter.image && (
              <div className="w-full">
                <img
                  src={chapter.image}
                  alt={chapter.title}
                  className="w-full h-48 sm:h-64 object-cover"
                  loading="lazy"
                />
              </div>
            )}

            <div className={`p-4 sm:p-6 ${isChild ? `bg-gradient-to-r ${color} text-white` : "bg-muted/50"}`}>
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-2xl sm:text-3xl shrink-0">{chapter.icon}</span>
                <div className="min-w-0">
                  <p className={`text-xs font-medium ${isChild ? "text-white/70" : "text-muted-foreground"}`}>{t("psychoed.chapter")} {currentChapter + 1} / {chapters.length}</p>
                  <h2 className={`font-heading text-base sm:text-xl font-bold break-words ${isChild ? "text-white" : ""}`}>{chapter.title}</h2>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              {chapter.paragraphs.map((p, i) => (
                <div key={i} className={`${isChild ? "text-sm sm:text-base leading-relaxed" : "text-sm leading-relaxed text-muted-foreground"} ${isChild && i % 2 === 0 ? "pl-3 sm:pl-4 border-l-4 border-primary/30" : ""} break-words`} style={{overflowWrap: 'anywhere'}}>
                  <p>{p}</p>
                </div>
              ))}
            </div>

            {chapter.quiz && (
              <div className={`mx-4 sm:mx-6 mb-4 sm:mb-6 p-4 sm:p-5 rounded-xl ${isChild ? "bg-white/80 dark:bg-black/20" : "bg-muted/50"} border`}>
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <HelpCircle className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 ${isChild ? "text-primary" : "text-muted-foreground"}`} />
                  <h3 className="font-heading font-semibold">{isChild ? t("psychoed.quizChild") : t("psychoed.quizAdult")}</h3>
                </div>
                <p className="font-medium mb-3 text-sm sm:text-base break-words">{chapter.quiz.question}</p>
                <div className="space-y-2">
                  {chapter.quiz.options.map((opt, oi) => {
                    const selected = quizAnswers[chapter.id] === oi;
                    const revealed = showQuizResult[chapter.id];
                    const isCorrect = oi === chapter.quiz.correct;
                    let btnClass = "w-full text-left p-2.5 sm:p-3 rounded-lg border transition-all text-xs sm:text-sm break-words ";
                    if (revealed && isCorrect) {
                      btnClass += "bg-emerald-100 border-emerald-400 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200";
                    } else if (revealed && selected && !isCorrect) {
                      btnClass += "bg-red-100 border-red-400 text-red-800 dark:bg-red-950 dark:border-red-700 dark:text-red-200";
                    } else if (!revealed) {
                      btnClass += "hover:bg-primary/5 hover:border-primary/30 cursor-pointer";
                    } else {
                      btnClass += "opacity-60";
                    }
                    return (
                      <button key={oi} className={btnClass} onClick={() => handleQuizAnswer(chapter.id, oi)} disabled={revealed} data-testid={`quiz-option-${oi}`}>
                        <div className="flex items-start gap-2">
                          {revealed && isCorrect && <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
                          {revealed && selected && !isCorrect && <span className="text-red-600 font-bold shrink-0">X</span>}
                          <span className="break-words">{opt}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {showQuizResult[chapter.id] && (
                  <div className={`mt-4 p-3 rounded-lg ${quizAnswers[chapter.id] === chapter.quiz.correct ? "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300"}`}>
                    <p className="text-sm">
                      {quizAnswers[chapter.id] === chapter.quiz.correct ? (isChild ? t("psychoed.correctChild") : t("psychoed.correctAdult")) : (isChild ? t("psychoed.wrongChild") : t("psychoed.wrongAdult"))}
                      {chapter.quiz.explanation}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} disabled={currentChapter === 0} data-testid="prev-chapter-btn" className="text-xs sm:text-sm">
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> {t("psychoed.prev")}
          </Button>
          {isLastChapter && chaptersCompleted.includes(chapter.id) ? (
            <Button size="sm" onClick={handleSave} disabled={saving || !allCompleted} className={`gap-1 sm:gap-2 text-xs sm:text-sm ${isChild ? `bg-gradient-to-r ${color} hover:opacity-90` : "bg-primary hover:bg-primary/90"}`} data-testid="save-module-btn">
              <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="truncate max-w-[120px] sm:max-w-none">
                {saving ? t("psychoed.saving") : (allCompleted ? t("psychoed.complete") : `${chaptersCompleted.length}/${chapters.length} ${t("psychoed.chapters")}`)}
              </span>
            </Button>
          ) : (
            <Button size="sm" onClick={goNext} disabled={currentChapter === chapters.length - 1} data-testid="next-chapter-btn" className="text-xs sm:text-sm">
              {t("psychoed.next")} <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
            </Button>
          )}
        </div>

        {/* Exercices recommandés par le module */}
        {moduleData.recommended?.length > 0 && (
          <Card data-testid="module-recommended-exercises">
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-1"><Target className="w-4 h-4 text-primary" /> {t("psychoed.recommendedExercises", "Exercices recommandés")}</h3>
              <p className="text-xs text-muted-foreground mb-3">{t("psychoed.recommendedExercisesHint", "Pour mettre en pratique ce module. Votre thérapeute peut aussi vous les assigner.")}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {moduleData.recommended.map((r) => (
                  <button key={r.type} onClick={() => navigate(r.path)} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm text-left hover:bg-muted transition-colors" data-testid={`recommended-${r.type}`}>
                    <span>{r.i18nKey ? t(r.i18nKey, r.fallback) : getExerciseName(t, r.type, r.fallback)}{r.tag ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">{t(r.tag, r.tagFallback)}</span> : null}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4" /> {t("psychoed.historyTitle")} ({history.length})
              </h3>
              <div className="space-y-2">
                {history.slice(0, 5).map((entry, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50">
                    <span className="text-muted-foreground">
                      {new Date(entry.created_at || entry.submitted_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                    <span className="flex items-center gap-1 text-emerald-600">
                      <Check className="w-3.5 h-3.5" />
                      {entry.data?.chapters_completed?.length || 0}/{entry.data?.total_chapters || chapters.length} {t("psychoed.chapters")}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default PsychoeducationPage;
